import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { CircularProgress } from '@mui/material'

import { mutations } from '../../../graphql'
import { Button, CardBox, Stack, Container, Fields, useAlert } from '../../../components'
import { fromAmountField, getTrackingData } from '../../../utils'

const GIVEWISE_APP_ORIGIN_STR = 'givewise-react-native-app'

export function PayButton({ customer }) {
  const { fundId } = useParams()
  const client = useApolloClient()
  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const { register, handleSubmit, getValues, formState, reset } = useForm({
    defaultValues: { amount: '0' },
  })

  const { errors = {} } = formState
  const [version, setVersion] = useState(0)
  const [status, setStatus] = useState('idle') // 'idle' | 'processing' | 'success' | 'fail'

  const sessionIdRef = useRef(null)
  const processedRef = useRef(false)

  const startNewSession = () => {
    processedRef.current = false
    sessionIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  const cleanupAfterAttempt = (softReset = true) => {
    if (window?.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'reset', sessionId: sessionIdRef.current }))
    }
    if (softReset) {
      reset({ amount: '0' })
      setStatus('idle')
      setVersion((v) => v + 1) // force remount of form subtree
    }
  }

  const handleMessage = useCallback(
    async (event) => {
      let data
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      } catch {
        return
      }

      if (!data || (data.sessionId && data.sessionId !== sessionIdRef.current)) return
      if (processedRef.current) return

      // Filter out unrelated messages
      const isMessageFromGwApp = 'origin' in data && data.origin === GIVEWISE_APP_ORIGIN_STR
      if (!isMessageFromGwApp) return

      processedRef.current = true

      try {
        const trackingData = getTrackingData()
        if (data.status === 'success') {
          await client.mutate({
            mutation: mutations.contributions.createContributionRecord,
            variables: {
              fundId: Number(fundId),
              amount: fromAmountField(data.amount),
              paymentType: 12,
              description: 'Apple Pay',
              taxReceipted: true,
              state: 1,
              paymentIntentId: data.paymentIntentId,
              ...trackingData,
            },
          })
          setStatus('success')
        } else if (data.status === 'fail') {
          // eslint-disable-next-line no-console
          console.error(`Payment failed. Error code: ${data.errorCode}, Message: ${data.errorMessage}`)
          await client.mutate({
            mutation: mutations.contributions.createContributionRecord,
            variables: {
              fundId: Number(fundId),
              amount: fromAmountField(data.amount),
              paymentType: 12,
              description: 'Apple Pay',
              taxReceipted: true,
              state: 4,
              ...trackingData,
            },
          })
          setStatus('fail')
        } else {
          processedRef.current = false
          return
        }
      } catch (e) {
        setAlert({
          message: `Failed to record contribution: ${e?.message ?? 'unknown error'}`,
          error: e?.message,
          severity: 'error',
        })
        setStatus('fail')
      } finally {
        window.removeEventListener('message', handleMessage)
        setTimeout(() => cleanupAfterAttempt(true), 1200)
      }
    },
    [client, fundId, reset, setAlert]
  )

  const handlePay = useCallback(() => {
    if (!window?.ReactNativeWebView?.postMessage) {
      setAlert({ message: 'Apple Pay is not available in this context.', severity: 'error' })
      return
    }
    const amt = getValues().amount
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'startPayment',
        sessionId: sessionIdRef.current,
        price: amt,
      })
    )
  }, [getValues, setAlert])

  const onSubmit = async () => {
    try {
      startNewSession()
      setStatus('processing')
      window.addEventListener('message', handleMessage, { once: true })
      handlePay()
    } catch (e) {
      window.removeEventListener('message', handleMessage)
      setStatus('idle')
      setAlert({
        message: `Failed to initiate payment: ${e?.message ?? ''}`,
        error: e?.message,
        severity: 'error',
      })
    }
  }

  // concise arrow body (fixes arrow-body-style)
  useEffect(
    () => () => {
      window.removeEventListener('message', handleMessage)
    },
    [handleMessage]
  )

  const renderActionArea = () => {
    if (status === 'processing') {
      return (
        <>
          <p>Processing Apple Pay…</p>
          <CircularProgress size={24} />
        </>
      )
    }
    if (status === 'success') {
      return (
        <>
          <p>Payment processed successfully!</p>
          <CircularProgress size={24} />
        </>
      )
    }
    if (status === 'fail') {
      return (
        <>
          <p>Payment failed. You can try again.</p>
          <Button type="button" onClick={() => cleanupAfterAttempt(true)}>
            Reset
          </Button>
        </>
      )
    }
    return (
      <Button type="submit" sx={{ padding: '10px 20px', fontSize: '16px' }}>
        Pay with Apple Pay
      </Button>
    )
  }

  return (
    <Container maxWidth="lg">
      <CardBox key={version} title="Pay with Apple Pay">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Fields.Text
              label="Amount"
              name="amount"
              error={!!errors?.amount}
              helperText={errors?.amount?.message}
              InputProps={{ startAdornment: <span>$</span> }}
              disabled={status === 'processing'}
              {...register('amount', {
                required: 'Amount is required',
                validate: {
                  positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
                  min: (value) => parseFloat(value) >= 1 || 'Must be at least 1',
                },
              })}
            />
            {renderActionArea()}
          </Stack>
        </form>
      </CardBox>
    </Container>
  )
}
